/**
 * Markdown → one line of plain text, for a Gmail-style preview under a list row.
 *
 * Work-order descriptions are markdown written by hand: bold banners, bulleted unit lists,
 * the odd link. A preview line shows the WORDS, so the syntax goes and every line break and
 * run of spaces collapses to one space. Truncation to the visible width is the renderer's
 * job (`numberOfLines`); `maxChars` only bounds how much text crosses into the row.
 */

const ESCAPE = /\\([\\`*_{}[\]()#+\-.!~>|])/g;
const PLACEHOLDER = '\u0000';

const stripLine = (line: string): string => line
	// Horizontal rules carry no words.
	.replace(/^\s*([-*_])(\s*\1){2,}\s*$/, '')
	// Block prefixes: heading marks, blockquotes, bullets and task boxes. Ordered-list
	// numbers stay — "1. Gutter" reads differently from "Gutter".
	.replace(/^\s*#{1,6}\s+/, '')
	.replace(/^\s*(>\s*)+/, '')
	.replace(/^\s*[-*+]\s+(\[[ xX]\]\s+)?/, '');

/**
 * Plain-text preview of a markdown string, or `undefined` when nothing readable is left.
 *
 * @param markdown  source text; `null`/`undefined` yield `undefined`
 * @param maxChars  upper bound on the returned length (default 300)
 */
export const toPlainTextPreview = (markdown: string | null | undefined, maxChars = 300): string | undefined => {
	if (!markdown) return undefined;
	const escaped: string[] = [];
	const text = markdown
		.replace(ESCAPE, (_, ch: string) => {
			escaped.push(ch);
			return PLACEHOLDER;
		})
		.split(/\r?\n/)
		.map(stripLine)
		.join('\n')
		// Code fences, then images and links → their text.
		.replace(/```[^\n]*/g, '')
		.replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
		.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
		// Emphasis, strikethrough and inline code markers. `_` only at word edges, so
		// snake_case survives; `*` runs only where they hug a word, so "5 * 3" survives.
		.replace(/(\*+)(?=\S)|(?<=\S)\*+/g, '')
		.replace(/(^|[^\w])_{1,2}(?=\S)|(?<=\S)_{1,2}(?=[^\w]|$)/g, '$1')
		.replace(/~~|`+/g, '')
		.replace(/\s+/g, ' ')
		.trim();
	let i = 0;
	const restored = text.replace(new RegExp(PLACEHOLDER, 'g'), () => escaped[i++] ?? '');
	if (!restored) return undefined;
	return restored.length > maxChars ? `${restored.slice(0, maxChars).trimEnd()}…` : restored;
};
